import {
  SearchableSelect,
  filterByLabel,
  useSearchableSelect,
} from "@circle-react-uikit/SearchableSelect";
import type { Options } from "@popperjs/core";
import { noop } from "lodash";
import type { ReactNode } from "react";

import { t } from "@/i18n-js/instance";

import { CreateLinkOption } from "./CreateLinkOption";
import { GroupedOptions } from "./GroupedOptions";
import { RegularOptions } from "./RegularOptions";

export interface TextSelectOption {
  group?: string;
  label: string;
  suffix?: ReactNode;
  value: string | number | null;
}

export interface TextSelectProps<
  Option extends TextSelectOption = TextSelectOption,
> {
  autoComplete?: string;
  buttonClassName?: string;
  createLinkLabel?: string;
  disabled?: boolean;
  displayValue?: (option: Option) => string;
  emptyMessage?: string;
  filterBy?: (
    options: Option[] | readonly Option[],
    search: string,
  ) => Option[];
  groupBy?: (option: Option) => string | undefined;
  hideCaret?: boolean;
  initialValue?: Option["value"];
  inputClassName?: string;
  isError?: boolean;
  label?: ReactNode;
  // Opt-in cap on the number of options rendered. Leave unset to render
  // every matching option (the default). When set and there are more
  // matches than the cap, a "keep typing" hint is shown instead of the rest.
  maxVisibleOptions?: number;
  onChange?: (value: Option["value"]) => void;
  onClear?: () => void;
  onCreateLinkClick?: (searchTerm: string) => void;
  onSearchChange?: (search: string) => void;
  options?: readonly Option[];
  placeholder?: string;
  popperOptions?: Partial<Options>;
  value?: Option["value"];
}

export const TextSelect = <Option extends TextSelectOption = TextSelectOption>({
  options = [],
  onChange = noop,
  onSearchChange = noop,
  isError = false,
  placeholder = t("select_an_option"),
  emptyMessage = t("no_options"),
  filterBy = filterByLabel,
  groupBy,
  displayValue = option => option?.label || "",
  initialValue = "",
  value,
  createLinkLabel,
  onCreateLinkClick,
  maxVisibleOptions,
  ...rest
}: TextSelectProps<Option>) => {
  const {
    search,
    selectProps,
    searchResults,
    hasNoResults,
    hasMoreResults,
    groupedSearchResults,
    hasGroups,
  } = useSearchableSelect<Option, Option["value"]>({
    isError,
    options,
    onChange,
    filterBy,
    placeholder,
    displayValue,
    onSearchChange,
    groupByValue: groupBy,
    initialValue,
    value,
    maxVisibleOptions,
  });

  return (
    <SearchableSelect {...rest} {...selectProps}>
      {hasNoResults && !createLinkLabel && (
        <SearchableSelect.EmptyOption label={emptyMessage} />
      )}
      {!hasNoResults && !hasGroups && (
        <RegularOptions options={searchResults} />
      )}
      {!hasNoResults && hasGroups && groupedSearchResults && (
        <GroupedOptions groupedOptions={groupedSearchResults} />
      )}
      {hasMoreResults && (
        <SearchableSelect.EmptyOption
          label={t("refine_search_for_more_options", {
            count: maxVisibleOptions,
          })}
        />
      )}
      {createLinkLabel && onCreateLinkClick && (
        <>
          {!hasNoResults && <p className="border-primary mb-2 border-b" />}
          <CreateLinkOption
            search={search}
            createLinkLabel={createLinkLabel}
            onClick={() => onCreateLinkClick(search)}
          />
        </>
      )}
    </SearchableSelect>
  );
};
